/** @jsxImportSource @emotion/react */

import React, { useEffect } from 'react';
import styled from 'styled-components';
import Scroll from '@component/common/scroll';
import * as size from '@constant/size';
import * as color from '@constant/color';
import { useTypedDispatch, useTypedSelector } from '@hook/common/useStore';
import IconBlock from '@component/common/block/icon-block';
import { actions as newsletterActions } from '@store/slice/newsletter-slice';
import ShelfContent from '@component/moashelf/newsletter/shelf-content';
import { useShelfQuery } from '@hook/moashelf/useApi';
import TextBlock from '@component/common/block/text-block';
import { formatDate, formatEllipsis, removePrefix } from '@util/string';
import { useModifyShelfMutation } from '@hook/moashelf/useApi';
import TextInput from '@component/common/input/text-input';
import Gap from '@component/common/gap';
import { isMobile } from 'react-device-detect';
import { useNavigate } from 'react-router-dom';
import { MOASHELF_PATH } from '@constant/moashelf/route-path';

interface IProps {
  shelfKey: string;
}

const ICON_SIZE = '25px';

const Wrapper = styled.div`
  position: relative;
  height: 100%;
`;

const SummaryContainer = styled.div`
  width: calc(100% - 2 * ${size.MARGIN_1});
  margin: ${size.MARGIN_1} auto ${size.MARGIN_1} auto;
`;

const Header = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: ${size.WEB_SUB_HEADER_HEIGHT};
  width: 100%;
  padding: 0 ${size.MARGIN_1} 0 ${size.MARGIN_1};
  background-color: ${color.GRAY_4};
  box-shadow: ${color.SURFACE_SHADOW};
`;

const ShelfIconContainer = styled.div`
  display: flex;
  width: fit-content;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
`;

const HashtagContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const HashtagInputContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  position: relative;
`;

const HashtagSaveButtonContainer = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: ${size.WEB_SUB_HEADER_HEIGHT};
  height: 60px;
  width: 100%;
  gap: 10px;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: ${color.HEADER_SHADOW};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity 0.3s ease-in-out;
  z-index: 1;
`;

const ICON_DESIGN = {
  box: { width: ICON_SIZE, height: ICON_SIZE, event: { cursor: 'pointer' } },
  icon: { size: 'medium', color: color.GRAY_1, kind: 'outlined' },
} as const;

const VALUE_TEXT_DESIGN = {
  text: { size: 'medium' },
  box: { width: '200px', height: '25px', backgroundColor: color.WHITE_1, align: 'center', borderRadius: '5px' },
} as const;

const CHIP_DESIGN = {
  text: { size: 'medium', color: color.WHITE_1 },
  box: {
    width: 'fit-content',
    backgroundColor: color.YELLOW_2,
    borderRadius: '10px',
    padding: '1px 8px 1px 8px',
    multiline: 2,
    align: 'center',
  },
} as const;

const ErrorText = styled.div<{ isVisible: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: 0;
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  width: 100%;
`;

// max-width 넣기
const Hashtag: React.FC<any> = ({ isHashtagEdit, hashtag, newHashtag, handeClickHashtag, handleHashtagChange }) => {
  return (
    <HashtagContainer>
      {!isHashtagEdit ? (
        <>
          {hashtag ? (
            <TextBlock
              text={hashtag}
              design={{ ...CHIP_DESIGN, box: { ...CHIP_DESIGN.box, maxWidth: '200px' } }}
              click={{ handler: handeClickHashtag }}
            />
          ) : (
            <TextBlock
              text={'# 태그로 정리하기'}
              design={{ ...CHIP_DESIGN, box: { ...CHIP_DESIGN.box, maxWidth: '200px' } }}
              click={{ handler: handeClickHashtag }}
            />
          )}
        </>
      ) : (
        <HashtagInputContainer>
          <TextInput
            name={'hashtag'}
            placeholder={hashtag || '예) #경제 또는 #경제/재테크'}
            design={VALUE_TEXT_DESIGN}
            onChange={handleHashtagChange}
            value={newHashtag ?? null}
          />
        </HashtagInputContainer>
      )}
    </HashtagContainer>
  );
};

const HashtagSaveButton: React.FC<any> = ({
  isHashtagEdit,
  isHashtagError,
  handleHashtagSaveClick,
  handeHashtagEditCancelClick,
}) => {
  return (
    <HashtagSaveButtonContainer isVisible={isHashtagEdit}>
      <ErrorText isVisible={isHashtagError}>
        <TextBlock
          text={'해시태그는 최대 3단계 (#a, #a/b, #a/b/c) 형식만 허용됩니다.'}
          design={{ text: { size: 'tiny', color: color.ERROR_RED } }}
        />
      </ErrorText>
      <TextBlock
        text={'저장하기'}
        design={{ ...CHIP_DESIGN, box: { ...CHIP_DESIGN.box, width: '70px' } }}
        click={{ handler: handleHashtagSaveClick }}
      />
      <TextBlock
        text={'취소하기'}
        design={{ ...CHIP_DESIGN, box: { ...CHIP_DESIGN.box, width: '70px' } }}
        click={{ handler: handeHashtagEditCancelClick }}
      />
    </HashtagSaveButtonContainer>
  );
};

const Summary: React.FC<any> = ({ title, provider, publishedAt }) => {
  return (
    <SummaryContainer>
      <TextBlock
        text={title}
        design={{
          text: { size: 'medium', color: color.BLACK_2, bold: true, multiline: 2 },
          box: { width: '100%', height: 'auto', alignX: 'left' },
        }}
      />
      <TextBlock
        text={provider}
        design={{
          text: { size: 'medium' },
          box: { width: '100%', height: 'auto', alignX: 'left' },
        }}
      />
      <TextBlock
        text={formatDate(publishedAt)}
        design={{
          text: {
            size: 'tiny',
            color: color.GRAY_1,
          },
          box: {
            width: '100%',
            height: 'auto',
            alignX: 'left',
          },
        }}
      />
    </SummaryContainer>
  );
};

const Shelf: React.FC<IProps> = ({ shelfKey }) => {
  const dispatch = useTypedDispatch();
  const modifyShelfMutation = useModifyShelfMutation();
  const query = useShelfQuery(shelfKey);
  const navigate = useNavigate();

  const isHashtagEdit = useTypedSelector((state) => state.rootReducer?.newsletterReducer?.isHashtagEdit) ?? false;
  const newHashtag = useTypedSelector((state) => state.rootReducer?.newsletterReducer?.newHashtag) ?? null;
  const isHashtagValid = useTypedSelector((state) => state.rootReducer?.newsletterReducer?.isHashtagValid) ?? false;
  const isHashtagError = useTypedSelector((state) => state.rootReducer?.newsletterReducer?.isHashtagError) ?? false;

  const content = query?.data?.data?.article?.content || '<div></div>'; // TODO: Loading Spinner
  const title = removePrefix(query?.data?.data?.article.title ?? '', '[Catch] ');
  const provider = (query?.data?.data?.article?.provider.name || query?.data?.data?.article.provider.email) ?? '';
  const publishedAt = query?.data?.data?.article?.publishedAt ?? null;
  const isBookmark = query?.data?.data?.isBookmark;
  const isTrash = !!query?.data?.data?.trashedAt;
  const hashtag = query?.data?.data?.hashtag;

  const handleShelfCloseClick = () => {
    // dispatch(newsletterActions.clickShelfClose());
    // navigate(-1);
    navigate(MOASHELF_PATH.SHELF_PATH);
  };
  const handeClickHashtag = () => dispatch(newsletterActions.clickHashtag());
  const handleHashtagChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (isHashtagError) dispatch(newsletterActions.hideHashtagError());
    dispatch(newsletterActions.editHashtag(ev.target));
  };
  const handleHashtagSaveClick = async () => {
    if (!isHashtagValid) return dispatch(newsletterActions.showHashtagError());
    await modifyShelfMutation.mutateAsync({ shelfKey, hashtag: newHashtag || undefined });
    dispatch(newsletterActions.completeHashtagEdit());
  };
  const handeHashtagEditCancelClick = () => dispatch(newsletterActions.cancelHashtagEdit());
  const handleBookmarkClick = async () => {
    await modifyShelfMutation.mutateAsync({ shelfKey, isBookmark: !isBookmark });
  };
  const handleTrashClick = async (ev) => {
    await modifyShelfMutation.mutateAsync({ shelfKey, isTrash: true });
    navigate(MOASHELF_PATH.SHELF_PATH);
  };
  const handleUntrashClick = async (ev) => {
    await modifyShelfMutation.mutateAsync({ shelfKey, isTrash: false });
  };

  // 읽음 처리
  useEffect(() => {
    modifyShelfMutation.mutate({ shelfKey, isRead: true });
  }, []);

  return (
    <Wrapper>
      <Header>
        <ShelfIconContainer>
          <IconBlock
            icon='star'
            design={{
              ...ICON_DESIGN,
              icon: { ...ICON_DESIGN.icon, checkedColor: color.YELLOW_1, checked: isBookmark },
            }}
            click={{ handler: handleBookmarkClick }}
          />
          {isTrash ? (
            <IconBlock icon='untrash' design={ICON_DESIGN} click={{ handler: handleUntrashClick }} />
          ) : (
            <IconBlock
              icon='trash'
              design={{ ...ICON_DESIGN, icon: { ...ICON_DESIGN.icon, size: 'medium' } }}
              click={{ handler: handleTrashClick }}
            />
          )}
        </ShelfIconContainer>
        {/*<Hashtag*/}
        {/*  isHashtagEdit={isHashtagEdit}*/}
        {/*  hashtag={hashtag}*/}
        {/*  newHashtag={newHashtag}*/}
        {/*  handeClickHashtag={handeClickHashtag}*/}
        {/*  handleHashtagChange={handleHashtagChange}*/}
        {/*/>*/}
        <IconBlock icon='close' design={ICON_DESIGN} click={{ handler: handleShelfCloseClick }} />
      </Header>
      <HashtagSaveButton
        isHashtagEdit={isHashtagEdit}
        isHashtagError={isHashtagError}
        handleHashtagSaveClick={handleHashtagSaveClick}
        handeHashtagEditCancelClick={handeHashtagEditCancelClick}
      />
      <Summary title={title} provider={provider} publishedAt={publishedAt} />
      <Scroll direction='y' height={`calc(100% - ${size.MOBILE_HEADER_HEIGHT} - ${size.WEB_SUB_HEADER_HEIGHT})`}>
        <ShelfContent content={content} />
      </Scroll>
    </Wrapper>
  );
};

export default Shelf;
